import React from 'react';
import { Alert, Box, Button, LinearProgress } from '@mui/material';
import store from '../stores/useVoyagerStore';
import studio from '../stores/useStudioStore';
import { getAllPerspectives } from '../apis';
import { invalidateNavigationLoads } from './loadEpoch';
import { getPersistenceNamespace } from '../stores/helpers/persistenceHelpers';
import { parseRoute, routeFor, Route } from './routes';
import { preferredStep } from '../stores/slices/workflowsSlice';
import { SEMANTIC_LAYER_TABS, STUDIO_HOME_TABS, StudioHomeTab } from '../stores/useStudioStore';
import { studioTabFromParam } from '../components/Studio/studioTabs';
import { NEW_OBJECTIVE, OBJECTIVE_TABS, type ObjectiveTab } from '../stores/slices/navigationSlice';

/** Keep the existing store actions as the navigation seam for every caller. */
export default function PageNavigation({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const generation = store(s => s.threadsGeneration);
  React.useEffect(() => {
    const namespace = getPersistenceNamespace();
    let disposed = false, applying = true, failed = false, version = 0;
    let controller = new AbortController();
    let progressRoute: Route | undefined;
    let last = window.location.pathname + window.location.search;
    let position = Number.isInteger(window.history.state?.voyagerPosition) ? window.history.state.voyagerPosition : window.history.length - 1;
    window.history.replaceState({ ...window.history.state, voyagerPosition: position }, '', last);
    let reverting = false;
    const cancelTraversal = () => {
      const target = window.history.state?.voyagerPosition;
      if (Number.isInteger(target) && target !== position) { reverting = true; window.history.go(position - target); }
      else window.history.replaceState({ ...window.history.state, voyagerPosition: position }, '', last);
    };
    const restore = () => {
      if (reverting) { reverting = false; return; }
      const requested = new URL(window.location.href);
      const target = parseRoute(requested);
      const draft = studio.getState();
      // Stroma's Studio home keeps the draft in memory, so going there discards nothing (#566).
      const toStudioHome = target?.view === 'studio' && !target.id;
      if (target?.view === 'studio' && !toStudioHome && target.id !== draft.draftModel?.id && draft.isDirty &&
          !window.confirm('Leave this draft and discard unsaved changes?')) {
        cancelTraversal(); return;
      }
      if (target?.view === 'workflows' && target.id !== store.getState().currentWorkflow?.id && store.getState().isDirty &&
          !window.confirm('Leave this workflow and discard unsaved changes?')) {
        cancelTraversal(); return;
      }
      if (Number.isInteger(window.history.state?.voyagerPosition)) position = window.history.state.voyagerPosition;
      invalidateNavigationLoads();
      controller.abort(); controller = new AbortController();
      store.setState({ isLoading: false, dashboardsLoading: false, navigationPending: 0 });
      studio.setState({ isLoading: false });
      const signal = controller.signal;
      failed = false;
      const ticket = ++version;
      const sameDraft = target?.view === 'studio' && target.id === studio.getState().draftModel?.id && store.getState().currentView === 'studio';
      applying = true; if (!sameDraft) setLoading(true); setError('');
      const showDefinition = async (id?: string) => {
        if (!id) { store.setState({ selectedDefinitionId: null }); return; }
        await store.getState().fetchDefinitions();
        if (disposed || ticket !== version) return;
        if (store.getState().definitionsError) throw new Error('Could not load definitions. Try again.');
        if (!store.getState().definitions.some(d => d.id === id)) throw new Error('Definition unavailable in this workspace.');
        // a linked definition is shown even when the list was filtered to hide it
        if (store.getState().selectedDefinitionId !== id) store.setState({ selectedDefinitionId: id, definitionsFilter: 'All', definitionsSearch: '' });
      };
      void (async () => {
        if (disposed || ticket !== version) return;
        const r = parseRoute(requested);
        try {
          if (!r) throw new Error('This page does not exist.');
          const s = store.getState();
          store.setState({ currentView: r.view });
          if (r.report) {
            store.setState({ loadedReportId: null, semanticModel: null });
            if (!await s.loadReport(r.report, signal)) throw new Error('Report unavailable in this workspace.');
          } else if (r.session) {
            s.clearLandingChat();
            if (!await s.restoreLandingAgentSession(r.session, signal)) throw new Error('Session unavailable in this workspace.');
          } else if (r.view === 'landing' && r.id) {
            await s.fetchThreads();
            if (disposed || ticket !== version) return;
            if (store.getState().threadsError) throw new Error('Could not load threads. Try again.');
            if (!store.getState().savedThreads.some(t => t.id === r.id)) throw new Error('Thread unavailable in this workspace.');
            s.loadThread(r.id);
            store.setState({ threadTurnAnchor: r.turn ? { threadId: r.id, turnId: r.turn } : null, threadRailParked: r.parked === '1' ? r.id : null });
          } else if (r.view === 'landing' && !requested.searchParams.has('session')) {
            s.clearLandingChat();
          } else if (r.view === 'studio' && !r.id) {
            // the Semantic layer's own tabs (#1510) open too
            const home = ([...STUDIO_HOME_TABS, ...SEMANTIC_LAYER_TABS] as readonly string[]).includes(r.tab || '') ? r.tab as StudioHomeTab : 'perspectives';
            studio.setState({ studioHome: home });
            if (home === 'definitions') await showDefinition(r.definition);
          } else if (r.view === 'studio') {
            if (r.id && studio.getState().draftModel?.id !== r.id) { studio.setState({ draftModel: null, baselineModel: null }); await studio.getState().loadDraft(r.id, signal); if (signal.aborted) return; if (studio.getState().draftModel?.id !== r.id) throw new Error('Draft unavailable in this workspace.'); }
            else if (!r.id) studio.setState({ draftModel: null, baselineModel: null, isDirty: false });
            studio.setState({ studioHome: null });
            studio.getState().setActivePreviewTab(studioTabFromParam(r.tab));
          } else if (r.view === 'connections') {
            await s.fetchConnections();
            if (disposed || ticket !== version) return;
            if (store.getState().connectionsError) throw new Error('Could not load connections. Try again.');
            if (r.id && !store.getState().connections.some(c => c.id === r.id)) throw new Error('Connection unavailable in this workspace.');
            s.selectConnection(r.id || null);
          } else if (r.view === 'dashboards') {
            s.closeDashboard();
            if (!r.id) await s.fetchDashboards();
            if (r.id) { await s.openDashboardById(r.id, signal); if (store.getState().currentDashboard?.id !== r.id) throw new Error('Dashboard unavailable in this workspace.'); }
          } else if (r.view === 'workflows') {
            if (r.id !== store.getState().currentWorkflow?.id) {
              s.wfClose();
              if (r.id) { try { await s.wfOpen(r.id, signal); } catch { throw new Error('Workflow unavailable in this workspace.'); } }
            }
            if (disposed || ticket !== version) return;
            const wf = store.getState().currentWorkflow;
            if (r.id && wf?.id !== r.id) throw new Error('Workflow unavailable in this workspace.');
            if (wf) {
              if (!r.run) store.setState({ lastExecution: null });
              else if (store.getState().lastExecution?.id !== r.run && !await s.wfShowRun(r.run, signal)) {
                if (disposed || ticket !== version) return;
                throw new Error('Run unavailable in this workspace.');
              }
              if (disposed || ticket !== version) return;
              const run = store.getState().lastExecution;
              // a step is the graph's (saved or not) or the run's; one that is neither (never saved, or deleted
              // since) falls back to the default step with a note, never replacing the editor with an error (#516)
              const known = !!r.step && (wf.steps.nodes.some(n => n.id === r.step)
                || (!!run && Object.prototype.hasOwnProperty.call(run.step_results || {}, r.step)));
              store.setState({
                selectedNodeId: (known && r.step) || (run ? preferredStep(run, wf.steps.nodes) : wf.steps.nodes.find(n => n.type === 'start.trigger')?.id || null),
                wfMissingStep: r.step && !known ? { workflowId: wf.id, stepId: r.step } : null,
              });
            }
          } else if (r.view === 'agent_wall') {
            if (!r.id) throw new Error('An agent ID is required.');
            // an agent link is its objective (#571); `?parked=1` is the parked wall itself
            s.navigateToAgentWall(r.id, r.tab === 'knowledge' ? 'knowledge' : 'wall', { parked: r.parked === '1' });
          } else if (r.view === 'objectives') {
            if (r.id === NEW_OBJECTIVE) {
              s.navigateToNewAgent();
              // a proposal's link (#575) seeds the draft from its words; the canonical address no longer carries them
              if (r.text) store.setState({ newAgentObjective: { text: r.text } });
            }
            else if (r.id) s.navigateToObjective(r.id, (OBJECTIVE_TABS as readonly string[]).includes(r.tab || '') ? r.tab as ObjectiveTab : 'plan');
            else s.navigateToObjectives();
          } else if (r.view === 'pipelines') {
            s.navigateToPipelines(r.id || null);
          } else if (r.view === 'pipeline_brain') {
            s.navigateToPipelineBrain(r.subject || null);
          } else if (r.view === 'builds') {
            // a build is its job id (#1624): the page decides what this viewer may see of it
            s.navigateToBuilds(r.id || null);
          } else if (r.view === 'workspace' && r.id) {
            store.setState({ semanticModel: null });
            await s.fetchDomains(signal);
            if (disposed || ticket !== version) return;
            const perspective = (await getAllPerspectives()).find(p => p.id === r.id);
            if (disposed || ticket !== version) return;
            if (!perspective) throw new Error('Perspective unavailable in this workspace.');
            await s.navigateToWorkspace(perspective?.domain || '', r.id, signal);
            if (!store.getState().semanticModel) throw new Error('Perspective unavailable in this workspace.');
            store.setState({ queryOrigin: r.from ? { threadId: r.from, turnId: r.turn || null } : null });
          } else if (r.view === 'workspace' || r.view === 'browse' || (r.view === 'brain' && r.tab === 'catalog')) {
            // the catalog is the Query Builder's landing (#577): a Discover or Catalog link opens it
            s.navigateToLanding();
            s.openWorkspace();
            store.setState({ orderBy: [], activeDateField: null, expandedNodes: new Set(), focusedNodeId: null, error: null, errorDetails: null, isLoading: false });
          } else if (r.view === 'activity') {
            store.setState({ activityTab: (['posts', 'changes', 'events'].includes(r.tab || '') ? r.tab : 'posts') as any, activityAgentId: r.agent || null, activityKinds: (r.kinds || '').split(',').filter(k => ['agent', 'post', 'promise', 'knowledge'].includes(k)) });
          } else if (r.view === 'brain') {
            const tabs = ['live', 'catalog', 'definitions', 'graph', 'claims', 'sources', 'topics', 'review'];
            let tab = (tabs.includes(r.tab || '') ? r.tab : r.topic ? 'topics' : undefined) as any;
            // no tab named: Knowledge's own first tab, which the simplified app's flag decides (#1511; fails closed to Topics)
            if (!tab) { await s.ensureFeatures().catch(() => undefined); if (disposed || ticket !== version) return; }
            // the focused lens (#578) is Knowledge's
            if (tab === 'graph' && r.lens) s.openLens(r.lens);
            else s.navigateToBrain(tab, r.metric, r.topic);
            if (tab === 'definitions') await showDefinition(r.definition);
          } else if (r.view === 'definitions') {
            // a definitions link lands in Studio › Definitions (#566)
            store.setState({ currentView: 'studio' });
            studio.setState({ studioHome: 'definitions' });
            await showDefinition(r.definition);
          } else if (r.view === 'metric-graph') {
            store.setState({ brainMetric: r.metric || null });
          } else if (r.view === 'agent_studio') {
            let tab = (['appearance', 'workspace', 'evidence', 'extension', 'mcp', 'connectors', 'company-brain', 'agents', 'refresh', 'access', 'models', 'support', 'parked', 'explanations', 'watches'].includes(r.tab || '') ? r.tab : 'appearance') as any;
            // support is a platform owner's tab: ask who this is before deciding what the address shows
            if (tab === 'support') { await s.ensureFeatures(); if (disposed || ticket !== version) return; if (!store.getState().platformOwner) tab = 'connectors'; }
            s.navigateToAgentStudio(tab);
          }
          if (disposed || ticket !== version) return;
          progressRoute = r.view === 'landing' && r.id ? r : undefined;
          last = routeFor(store.getState(), studio.getState(), progressRoute);
          if (requested.searchParams.get('view') !== 'extension') window.history.replaceState({ ...window.history.state, voyagerPosition: position }, '', last);
        } catch (e) {
          if (!disposed && ticket === version) { failed = true; setError(e instanceof Error ? e.message : 'This resource is unavailable.'); }
        } finally {
          if (!disposed && ticket === version) { applying = false; setLoading(false); }
        }
      })();
    };
    const sync = () => { queueMicrotask(() => {
      if (disposed || store.getState().threadsGeneration !== generation || getPersistenceNamespace() !== namespace || applying || failed || reverting || (store.getState().currentView === 'workspace' && store.getState().navigationPending > 0)) return;
      if (store.getState().currentView !== 'landing' || store.getState().currentThreadId !== progressRoute?.id) progressRoute = undefined;
      const next = routeFor(store.getState(), studio.getState(), progressRoute);
      if (next !== last) { last = next; window.history.pushState({ voyagerPosition: ++position }, '', next); setError(''); }
    }); };
    restore();
    const off = store.subscribe(sync), offStudio = studio.subscribe(sync);
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (studio.getState().isDirty || store.getState().isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('popstate', restore);
    return () => { disposed = true; controller.abort(); off(); offStudio(); window.removeEventListener('popstate', restore); window.removeEventListener('beforeunload', beforeUnload); };
  }, [generation]);
  if (loading) return <Box sx={{ p: 3 }}><LinearProgress />Opening page…</Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert><Button href="/">Go home</Button></Box>;
  return <>{children}</>;
}
